import { Modal, Tabs, Table, Badge, Text, Stack, Group, ScrollArea, Button, Textarea, Select, Checkbox, Code, Loader, CopyButton, ActionIcon, Tooltip, Switch, Alert } from '@mantine/core'
import { IconFileText, IconList, IconSend, IconCode, IconCopy, IconCheck, IconHistory, IconInfoCircle } from '@tabler/icons-react'
import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import type { ComplianceDashboardData, EvaluationVersion, DecisionVersion } from '@/types'
import type { FeedbackRequest } from '@/types/feedback.types'
import { segmentOffense, getErrorType, adjudicationCategoryValue } from '@/utils/dataProcessing'
import { useFeedbackApi } from '@/hooks/useFeedbackApi'
import { useEvaluationsBySearch, useDecisionHistory } from '@/hooks/useAnalytics'
import { notifications } from '@mantine/notifications'
import { getBronzeResponse } from '@/services/analytics.api'

// Fields restricted from expert users (internal analytics fields)
const EXPERT_RESTRICTED_FIELDS = [
  'Type Class',
  'Subtype Class',
  'Charge Normalized',
  'Disposition Class',
  'Disposition Normalized',
  'Offense Age',
  'Age Category',
  'Age at Offense',
  'Offense Status',
  'Adjudication',
  'Crime Category',
  'Court Type',
  'Est. Release Date',
  'RE Decision',
]

const QUEUE_DISPLAY_MAP: Record<string, string> = {
  AUTOMATION: 'Not Reportable',
  AUDITOR: 'Reportable',
  INSUFFICIENT_DATA: 'Manual Review',
  'INSUFFICIENT DATA': 'Manual Review',
}

interface OffenseDetailDialogProps {
  opened: boolean
  onClose: () => void
  offense: ComplianceDashboardData | null
  allOffensesInSearch: ComplianceDashboardData[]
  feedbackRequest?: FeedbackRequest | null  // Optional: for expert feedback mode
  onFeedbackSubmitted?: () => void          // Optional: callback after feedback submitted
  isExpert?: boolean                        // Optional: for expert user restrictions
  initialTab?: string                       // Optional: start on a specific tab (e.g. 'history')
}

export function OffenseDetailDialog({
  opened,
  onClose,
  offense,
  allOffensesInSearch,
  feedbackRequest,
  onFeedbackSubmitted,
  isExpert = false,
  initialTab,
}: OffenseDetailDialogProps) {
  // IMPORTANT: All hooks must be called before any conditional returns
  const { submitFeedback } = useFeedbackApi()

  // Feedback form state (for expert feedback mode)
  const [decision, setDecision] = useState<'REPORTABLE' | 'NOT_REPORTABLE' | 'REQUIRES_REVIEW' | 'UNCLEAR' | null>(null)
  const [feedback, setFeedbackText] = useState('')
  const [groundTruthInvalid, setGroundTruthInvalid] = useState(false)
  const [activeTab, setActiveTab] = useState<string | null>(initialTab || 'details')
  const [showAllOffenses, setShowAllOffenses] = useState(false)

  useEffect(() => {
    if (opened && initialTab) {
      setActiveTab(initialTab)
    } else if (opened) {
      setActiveTab('details')
    }
    setShowAllOffenses(false)
  }, [opened, initialTab])

  const isExpertMode = !!feedbackRequest
  const isCompleted = feedbackRequest?.feedback_status === 'COMPLETED'

  const bronzeQuery = useQuery({
    queryKey: ['bronze-response', offense?.search_id],
    queryFn: () => getBronzeResponse(offense!.search_id),
    enabled: !!offense?.search_id && activeTab === 'source',
    staleTime: 5 * 60 * 1000,
  })

  const evaluationsQuery = useEvaluationsBySearch(
    activeTab === 'history' ? (offense?.search_id || null) : null
  )
  const decisionsQuery = useDecisionHistory(
    activeTab === 'history' ? (offense?.search_id || null) : null
  )

  const decisionsByVersion = useMemo(() => {
    const versions = decisionsQuery.data as DecisionVersion[] | undefined
    if (!versions?.length) return new Map<string, DecisionVersion[]>()
    const unsorted = new Map<string, DecisionVersion[]>()
    for (const d of versions) {
      const key = d.correlation_id
      if (!unsorted.has(key)) unsorted.set(key, [])
      unsorted.get(key)!.push(d)
    }
    const sorted = new Map(
      [...unsorted.entries()].sort((a, b) => {
        const aVer = a[1][0]?.version_number ?? 0
        const bVer = b[1][0]?.version_number ?? 0
        return aVer - bVer
      })
    )
    return sorted
  }, [decisionsQuery.data])

  const allOffenseIds = useMemo(() => {
    const versions = decisionsQuery.data as DecisionVersion[] | undefined
    if (!versions?.length) return [] as string[]
    return [...new Set(versions.map(d => d.offense_id))].sort()
  }, [decisionsQuery.data])

  const displayedOffenseIds = useMemo(() => {
    if (showAllOffenses || !offense?.offense_id) return allOffenseIds
    const currentId = String(offense.offense_id)
    if (allOffenseIds.includes(currentId)) return [currentId]
    return allOffenseIds
  }, [allOffenseIds, showAllOffenses, offense?.offense_id])

  const runDeltaSummary = useMemo(() => {
    if (decisionsByVersion.size <= 1) return null
    const runs = [...decisionsByVersion.entries()].map(([corrId, entries]) => {
      const counts = { REPORTABLE: 0, NOT_REPORTABLE: 0, REQUIRES_REVIEW: 0, OTHER: 0, total: entries.length }
      for (const e of entries) {
        if (e.decision === 'REPORTABLE') counts.REPORTABLE++
        else if (e.decision === 'NOT_REPORTABLE') counts.NOT_REPORTABLE++
        else if (e.decision === 'REQUIRES_REVIEW') counts.REQUIRES_REVIEW++
        else counts.OTHER++
      }
      return {
        correlationId: corrId,
        versionNumber: entries[0]?.version_number ?? 1,
        date: entries[0]?.created_at,
        modelVersion: entries[0]?.model_version,
        counts,
      }
    })
    return runs.slice(-3)
  }, [decisionsByVersion])

  // Submit feedback mutation (for expert mode)
  const submitFeedbackMutation = useMutation({
    mutationFn: () => {
      if (!feedbackRequest || !decision || !feedback) {
        throw new Error('Please fill all required fields')
      }
      return submitFeedback(feedbackRequest.feedback_request_id, {
        expert_decision: decision,
        expert_feedback: feedback,
        ground_truth_invalid: groundTruthInvalid,
      })
    },
    onSuccess: () => {
      notifications.show({
        title: 'Success',
        message: 'Feedback submitted successfully',
        color: 'green',
      })
      if (onFeedbackSubmitted) {
        onFeedbackSubmitted()
      }
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to submit feedback',
        color: 'red',
      })
    },
  })

  const handleSubmitFeedback = () => {
    if (!decision) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please select a decision',
        color: 'yellow',
      })
      return
    }
    if (!feedback.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please provide feedback',
        color: 'yellow',
      })
      return
    }
    submitFeedbackMutation.mutate()
  }

  // Now check if offense exists (after all hooks)
  if (!offense) return null

  const segment = segmentOffense(offense)
  const errorType = offense.error_type || getErrorType(offense.ce_decision, offense.ground_truth)

  // Helper to format dates that may come as BigQuery objects { value: "YYYY-MM-DD" }
  const formatDate = (value: any): string | null => {
    if (!value) return null
    try {
      // BigQuery dates may come as objects with a `value` property
      const dateValue = typeof value === 'object' && value?.value ? value.value : value
      if (!dateValue) return null
      const date = new Date(dateValue)
      if (isNaN(date.getTime())) return null
      return date.toLocaleDateString()
    } catch {
      return null
    }
  }

  // Helper to render field values
  const renderFieldValue = (value: any, isDateField: boolean = false) => {
    if (value === null || value === undefined || value === '') return <Text c="dimmed" size="sm">N/A</Text>

    // Handle date fields specially
    if (isDateField) {
      const formattedDate = formatDate(value)
      if (!formattedDate) return <Text c="dimmed" size="sm">N/A</Text>
      return <Text size="sm">{formattedDate}</Text>
    }

    if (Array.isArray(value)) {
      return <Text size="sm">{value.join(', ')}</Text>
    }

    // Check for BigQuery date object that wasn't marked as a date field
    if (typeof value === 'object' && value?.value && typeof value.value === 'string') {
      // It might be a date object, try to format it
      const possibleDate = formatDate(value)
      if (possibleDate) {
        return <Text size="sm">{possibleDate}</Text>
      }
      return <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(value, null, 2)}</Text>
    }

    if (typeof value === 'object') {
      return <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(value, null, 2)}</Text>
    }

    return <Text size="sm">{String(value)}</Text>
  }

  // Define all fields to display (isDate marks date fields for special formatting)
  const allDetailFields = [
    { label: 'Search ID', value: offense.search_id },
    { label: 'Offense ID', value: offense.offense_id },
    { label: 'Case Number', value: offense.case_number },
    { label: 'Charge', value: offense.charge },
    { label: 'Charge Normalized', value: offense.charge_norm },
    { label: 'Disposition', value: offense.disposition },
    { label: 'Disposition Class', value: offense.disp_class },
    { label: 'Disposition Normalized', value: offense.disposition_norm },
    { label: 'Offense Type', value: offense.offense_type },
    { label: 'Type Class', value: offense.type_class },
    { label: 'Subtype Class', value: offense.subtype_class },
    { label: 'File Date', value: (offense as any).file_date, isDate: true },
    { label: 'Disposition Date', value: offense.disposition_date, isDate: true },
    { label: 'Offense Age', value: offense.offense_age },
    { label: 'Age at Offense', value: offense.age_at_offense },
    { label: 'Age Category', value: offense.age_category },
    { label: 'Offense Status', value: offense.offense_status },
    { label: 'Crime Category', value: offense.crime_category },
    { label: 'Adjudication', value: adjudicationCategoryValue(offense) },
    { label: 'Court Type', value: offense.court_type },
    { label: 'Additional Info', value: offense.additional_info },
    { label: 'Sentence Info', value: offense.sentence_info },
    { label: 'Release Date', value: offense.effective_release_date, isDate: true },
    { label: 'Currently Serving', value: offense.sentence_is_serving == null ? undefined : offense.sentence_is_serving ? 'Yes' : 'No' },
    { label: 'RE Decision', value: offense.ce_decision },
    { label: 'AI Decision (Queue)', value: QUEUE_DISPLAY_MAP[String((offense as any).queue ?? '').toUpperCase()] || (offense as any).queue || undefined },
    { label: 'UBS Decision', value: offense.ground_truth },
    { label: 'Rules Applied', value: offense.rules_applied },
    { label: 'AI Rationale', value: offense.rationale },
    { label: 'Notes', value: offense.all_notes },
    { label: 'Validation Issues', value: (offense as any).validation_issues },
    { label: 'Validation Errors', value: (offense as any).validation_error_count },
    { label: 'Validation Warnings', value: (offense as any).validation_warning_count },
  ]

  // Filter out restricted fields for expert users
  const detailFields = isExpert
    ? allDetailFields.filter(field => !EXPERT_RESTRICTED_FIELDS.includes(field.label))
    : allDetailFields

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="xl"
      title={
        <Group>
          <Text fw={600}>Offense Details</Text>
          <Badge color={segment === 'EVALUABLE' ? 'green' : segment === 'REQUIRES_RESEARCH' ? 'orange' : 'red'}>
            {segment}
          </Badge>
          <Badge color={offense.match_status === 'MATCH' ? 'green' : 'red'}>
            {offense.match_status}
          </Badge>
          <Badge color={errorType === 'CORRECT' ? 'green' : errorType === 'FALSE_POSITIVE' ? 'orange' : errorType === 'FALSE_NEGATIVE' ? 'red' : 'gray'}>
            {errorType}
          </Badge>
        </Group>
      }
    >
      <Stack gap="md">
        <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="details" leftSection={<IconFileText size={16} />}>
            Offense Details
          </Tabs.Tab>
          {!isExpert && (
            <Tabs.Tab value="search" leftSection={<IconList size={16} />}>
              All Offenses in Search ({allOffensesInSearch.length})
            </Tabs.Tab>
          )}
          {!isExpert && (
            <Tabs.Tab value="source" leftSection={<IconCode size={16} />}>
              Source Data (Bronze)
            </Tabs.Tab>
          )}
          {!isExpert && (
            <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>
              History
            </Tabs.Tab>
          )}
          {isExpertMode && (
            <Tabs.Tab value="feedback" leftSection={<IconSend size={16} />}>
              {isCompleted ? 'Submitted Feedback' : 'Provide Feedback'}
            </Tabs.Tab>
          )}
        </Tabs.List>

        <Tabs.Panel value="details" pt="md">
          <ScrollArea h={500}>
            <Table striped highlightOnHover>
              <Table.Tbody>
                {detailFields.map((field) => (
                  <Table.Tr key={field.label}>
                    <Table.Td style={{ width: '40%' }}>
                      <Text fw={600} size="sm">{field.label}</Text>
                    </Table.Td>
                    <Table.Td>
                      {field.label === 'Validation Issues' && field.value ? (
                        <Group gap={4} wrap="wrap">
                          {String(field.value).split(' | ').map((issue, i) => (
                            <Badge key={i} size="sm" color="red" variant="light" style={{ maxWidth: '100%', whiteSpace: 'normal', height: 'auto', padding: '4px 8px' }}>
                              {issue.trim()}
                            </Badge>
                          ))}
                        </Group>
                      ) : field.label === 'Validation Errors' ? (
                        <Badge size="sm" color={Number(field.value) > 0 ? 'red' : 'green'}>
                          {field.value ?? 0}
                        </Badge>
                      ) : field.label === 'Validation Warnings' ? (
                        <Badge size="sm" color={Number(field.value) > 0 ? 'orange' : 'green'}>
                          {field.value ?? 0}
                        </Badge>
                      ) : (
                        renderFieldValue(field.value, field.isDate)
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Tabs.Panel>

        <Tabs.Panel value="search" pt="md">
          <ScrollArea h={500}>
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                Showing all {allOffensesInSearch.length} offense(s) associated with Search ID: {offense.search_id}
              </Text>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Offense ID</Table.Th>
                    <Table.Th>Case Number</Table.Th>
                    <Table.Th>Charge</Table.Th>
                    <Table.Th>Disposition</Table.Th>
                    <Table.Th>AI Decision</Table.Th>
                    <Table.Th>UBS Decision</Table.Th>
                    <Table.Th>Segment</Table.Th>
                    <Table.Th>Match</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {allOffensesInSearch.map((off) => {
                    const offSegment = segmentOffense(off)
                    const isCurrentOffense = off.offense_id === offense.offense_id

                    return (
                      <Table.Tr
                        key={off.offense_id}
                        style={{
                          backgroundColor: isCurrentOffense ? 'var(--mantine-color-blue-0)' : undefined,
                          fontWeight: isCurrentOffense ? 600 : undefined
                        }}
                      >
                        <Table.Td>
                          <Text size="sm">{off.offense_id}</Text>
                          {isCurrentOffense && <Badge size="xs" color="blue">Current</Badge>}
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={1}>{off.case_number || 'N/A'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={1}>{off.charge || 'N/A'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={1}>{off.disposition || 'N/A'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{off.ce_decision || 'N/A'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{off.ground_truth || 'N/A'}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            size="sm"
                            color={offSegment === 'EVALUABLE' ? 'green' : offSegment === 'REQUIRES_RESEARCH' ? 'orange' : 'red'}
                          >
                            {offSegment}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="sm" color={off.match_status === 'MATCH' ? 'green' : 'red'}>
                            {off.match_status}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                </Table.Tbody>
              </Table>
            </Stack>
          </ScrollArea>
        </Tabs.Panel>

        <Tabs.Panel value="source" pt="md">
          {bronzeQuery.isLoading && (
            <Stack align="center" py="xl">
              <Loader size="md" />
              <Text size="sm" c="dimmed">Loading source data from Bronze layer...</Text>
            </Stack>
          )}
          {bronzeQuery.isError && (
            <Text c="red" size="sm">Failed to load bronze data: {(bronzeQuery.error as Error).message}</Text>
          )}
          {bronzeQuery.isSuccess && !bronzeQuery.data && (
            <Text c="dimmed" size="sm">No bronze data found for this search.</Text>
          )}
          {bronzeQuery.isSuccess && bronzeQuery.data && (
            <Tabs defaultValue={bronzeQuery.data.rawXml ? 'xml' : 'json'}>
              <Tabs.List>
                {bronzeQuery.data.rawXml && (
                  <Tabs.Tab value="xml">Raw XML</Tabs.Tab>
                )}
                {bronzeQuery.data.responseJson && (
                  <Tabs.Tab value="json">Transformed JSON</Tabs.Tab>
                )}
              </Tabs.List>

              {bronzeQuery.data.rawXml && (
                <Tabs.Panel value="xml" pt="sm">
                  <Group justify="space-between" mb="xs" wrap="nowrap">
                    <Text size="xs" c="dimmed">
                      Source: eval_framework_requests &middot; {bronzeQuery.data.xmlPublishTime ? new Date(bronzeQuery.data.xmlPublishTime).toLocaleString() : ''}
                    </Text>
                    <CopyButton value={bronzeQuery.data.rawXml} timeout={2000}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? 'Copied' : 'Copy XML'} withArrow position="left">
                          <ActionIcon
                            color={copied ? 'teal' : 'gray'}
                            variant="subtle"
                            onClick={copy}
                            aria-label="Copy raw XML to clipboard"
                          >
                            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                  <ScrollArea h="calc(100vh - 250px)">
                    <Code block style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {bronzeQuery.data.rawXml}
                    </Code>
                  </ScrollArea>
                </Tabs.Panel>
              )}

              {bronzeQuery.data.responseJson && (
                <Tabs.Panel value="json" pt="sm">
                  <Group justify="space-between" mb="xs" wrap="nowrap">
                    <Text size="xs" c="dimmed">
                      Source: xml_transformer_api_responses &middot; {bronzeQuery.data.publishTime ? new Date(bronzeQuery.data.publishTime).toLocaleString() : ''}
                    </Text>
                    <CopyButton value={JSON.stringify(bronzeQuery.data.responseJson, null, 2)} timeout={2000}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? 'Copied' : 'Copy JSON'} withArrow position="left">
                          <ActionIcon
                            color={copied ? 'teal' : 'gray'}
                            variant="subtle"
                            onClick={copy}
                            aria-label="Copy transformed JSON to clipboard"
                          >
                            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                  <ScrollArea h="calc(100vh - 250px)">
                    <Code block style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {JSON.stringify(bronzeQuery.data.responseJson, null, 2)}
                    </Code>
                  </ScrollArea>
                </Tabs.Panel>
              )}
            </Tabs>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="history" pt="md">
          {(evaluationsQuery.isLoading || decisionsQuery.isLoading) && (
            <Stack align="center" py="xl">
              <Loader size="md" />
              <Text size="sm" c="dimmed">Loading evaluation history...</Text>
            </Stack>
          )}
          {(evaluationsQuery.isError || decisionsQuery.isError) && (
            <Text c="red" size="sm">
              Failed to load evaluation history.
              {evaluationsQuery.isError && decisionsQuery.isError
                ? ' Both evaluation runs and decision records failed to load.'
                : evaluationsQuery.isError
                  ? ' Evaluation runs failed to load.'
                  : ' Decision records failed to load.'}
            </Text>
          )}
          {evaluationsQuery.isSuccess && (
            <ScrollArea h={500}>
              <Stack gap="lg">
                {((evaluationsQuery.data as EvaluationVersion[])?.length || 0) <= 1 && (
                  <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
                    Only one evaluation run found. Historical re-runs before tracking was enabled are not tracked separately.
                    New evaluations going forward will appear as Run 2, Run 3, etc.
                  </Alert>
                )}

                {runDeltaSummary && runDeltaSummary.length > 1 && (
                  <Stack gap="xs">
                    <Text fw={600} size="sm">Run Comparison (last {runDeltaSummary.length} runs)</Text>
                    <Table withTableBorder withColumnBorders>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th></Table.Th>
                          {runDeltaSummary.map((run) => (
                            <Table.Th key={run.correlationId} ta="center">
                              <Text size="xs" fw={600}>Run {run.versionNumber}</Text>
                              <Text size="xs" c="dimmed">
                                {run.date ? new Date(run.date).toLocaleDateString() : ''}
                              </Text>
                            </Table.Th>
                          ))}
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {(['REPORTABLE', 'NOT_REPORTABLE', 'REQUIRES_REVIEW'] as const).map(decType => (
                          <Table.Tr key={decType}>
                            <Table.Td>
                              <Badge size="xs" color={decType === 'REPORTABLE' ? 'red' : decType === 'NOT_REPORTABLE' ? 'green' : 'orange'} variant="light">
                                {decType.replace('_', ' ')}
                              </Badge>
                            </Table.Td>
                            {runDeltaSummary.map((run, i) => {
                              const count = run.counts[decType]
                              const prev = i > 0 ? runDeltaSummary[i - 1].counts[decType] : null
                              const delta = prev != null ? count - prev : null
                              return (
                                <Table.Td key={run.correlationId} ta="center">
                                  <Group gap={4} justify="center">
                                    <Text size="sm" fw={600}>{count}</Text>
                                    {delta != null && delta !== 0 && (
                                      <Text size="xs" fw={600} c={delta > 0 ? (decType === 'REPORTABLE' ? 'red' : 'green') : (decType === 'REPORTABLE' ? 'green' : 'red')}>
                                        {delta > 0 ? `+${delta}` : delta}
                                      </Text>
                                    )}
                                  </Group>
                                </Table.Td>
                              )
                            })}
                          </Table.Tr>
                        ))}
                        <Table.Tr>
                          <Table.Td><Text size="xs" fw={600}>Total Offenses</Text></Table.Td>
                          {runDeltaSummary.map(run => (
                            <Table.Td key={run.correlationId} ta="center">
                              <Text size="sm" fw={600}>{run.counts.total}</Text>
                            </Table.Td>
                          ))}
                        </Table.Tr>
                      </Table.Tbody>
                    </Table>
                  </Stack>
                )}

                <Stack gap="xs">
                  <Text fw={600} size="sm">
                    Evaluation Runs ({(evaluationsQuery.data as EvaluationVersion[])?.length || 0} total)
                  </Text>
                  <Table striped highlightOnHover withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Run</Table.Th>
                        <Table.Th>Model Ver</Table.Th>
                        <Table.Th>Source</Table.Th>
                        <Table.Th>Record Decision</Table.Th>
                        <Table.Th>Queue</Table.Th>
                        <Table.Th>Timing</Table.Th>
                        <Table.Th>Date</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {((evaluationsQuery.data as EvaluationVersion[]) || []).map((ev, idx, arr) => {
                        const versionNum = arr.length - idx
                        const prevEv = arr[idx + 1]
                        const decisionChanged = prevEv && prevEv.record_decision !== ev.record_decision
                        const queueChanged = prevEv && prevEv.search_queue !== ev.search_queue
                        const silverEntry = ev.correlation_id ? decisionsByVersion.get(ev.correlation_id)?.[0] : null

                        return (
                          <Table.Tr
                            key={ev.correlation_id || idx}
                            style={{
                              backgroundColor: (decisionChanged || queueChanged) ? 'var(--mantine-color-orange-0)' : undefined,
                            }}
                          >
                            <Table.Td>
                              <Group gap={4}>
                                <Text size="sm" fw={600}>Run {versionNum}</Text>
                                {idx === 0 && <Badge size="xs" color="blue">latest</Badge>}
                                {decisionChanged && <Badge size="xs" color="orange">changed</Badge>}
                              </Group>
                            </Table.Td>
                            <Table.Td>
                              <Badge size="xs" variant="light" color="gray">
                                {silverEntry?.model_version || 'N/A'}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{ev.source || 'unknown'}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Badge
                                size="xs"
                                color={ev.record_decision === 'REPORTABLE' ? 'red' : ev.record_decision === 'NOT_REPORTABLE' ? 'green' : 'gray'}
                              >
                                {ev.record_decision || 'N/A'}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Badge
                                size="xs"
                                variant="light"
                                color={ev.search_queue?.toLowerCase() === 'automation' ? 'green' : ev.search_queue?.toLowerCase() === 'auditor' ? 'orange' : 'gray'}
                              >
                                {QUEUE_DISPLAY_MAP[String(ev.search_queue ?? '').toUpperCase()] || ev.search_queue || 'N/A'}
                              </Badge>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {ev.execution_time_ms != null ? `${(ev.execution_time_ms / 1000).toFixed(1)}s` : 'N/A'}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="xs" c="dimmed">
                                {ev.publish_time ? new Date(ev.publish_time).toLocaleString() : 'N/A'}
                              </Text>
                            </Table.Td>
                          </Table.Tr>
                        )
                      })}
                    </Table.Tbody>
                  </Table>
                </Stack>

                {decisionsQuery.isSuccess && allOffenseIds.length > 0 && (
                  <Stack gap="xs">
                    <Group justify="space-between" align="center">
                      <Text fw={600} size="sm">
                        Offense-Level Decision Comparison
                        {!showAllOffenses && allOffenseIds.length > 1 && (
                          <Text component="span" size="xs" c="dimmed" ml={4}>
                            (showing selected offense)
                          </Text>
                        )}
                      </Text>
                      {allOffenseIds.length > 1 && (
                        <Switch
                          label={`Show all ${allOffenseIds.length} offenses`}
                          size="xs"
                          checked={showAllOffenses}
                          onChange={(e) => setShowAllOffenses(e.currentTarget.checked)}
                        />
                      )}
                    </Group>
                    <ScrollArea>
                      <Table striped highlightOnHover withTableBorder>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>Offense ID</Table.Th>
                            <Table.Th>Charge</Table.Th>
                            {[...decisionsByVersion.entries()].map(([corrId, entries]) => {
                              const vNum = entries[0]?.version_number ?? 1
                              const date = entries[0]?.created_at
                              const isLatest = vNum === Math.max(...[...decisionsByVersion.values()].map(e => e[0]?.version_number ?? 1))
                              return (
                                <Table.Th key={corrId}>
                                  Run {vNum}
                                  {isLatest && <Badge size="xs" color="blue" ml={4}>latest</Badge>}
                                  <Text size="xs" c="dimmed" component="span" ml={4}>
                                    {date ? new Date(date).toLocaleDateString() : ''}
                                  </Text>
                                </Table.Th>
                              )
                            })}
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {displayedOffenseIds.map(offId => {
                            const versionKeys = [...decisionsByVersion.keys()]
                            const decisions = versionKeys.map(
                              corrId => decisionsByVersion.get(corrId)!.find(d => d.offense_id === offId)
                            )

                            const charge = decisions.find(d => d?.charge)?.charge || 'N/A'
                            const isCurrentOffense = offId === String(offense?.offense_id)
                            const hasChange = decisions.some((d, i) =>
                              i > 0 && d?.decision !== decisions[i - 1]?.decision
                              && d?.decision != null && decisions[i - 1]?.decision != null
                            )

                            return (
                              <Table.Tr key={offId} style={{
                                backgroundColor: isCurrentOffense
                                  ? 'var(--mantine-color-blue-0)'
                                  : hasChange ? 'var(--mantine-color-orange-0)' : undefined,
                                fontWeight: isCurrentOffense ? 600 : undefined,
                              }}>
                                <Table.Td>
                                  <Group gap={4}>
                                    <Text size="xs" ff="monospace">{offId}</Text>
                                    {isCurrentOffense && <Badge size="xs" color="blue">current</Badge>}
                                  </Group>
                                </Table.Td>
                                <Table.Td><Text size="xs" lineClamp={1}>{charge}</Text></Table.Td>
                                {decisions.map((d, i) => {
                                  const prev = i > 0 ? decisions[i - 1] : null
                                  const changed = prev && d && prev.decision !== d.decision
                                  return (
                                    <Table.Td key={i}>
                                      {d ? (
                                        <Tooltip label={d.rules_applied || 'No rules'} multiline maw={300}>
                                          <Badge
                                            size="sm"
                                            color={d.decision === 'REPORTABLE' ? 'red' : d.decision === 'NOT_REPORTABLE' ? 'green' : 'gray'}
                                            variant={changed ? 'filled' : 'light'}
                                          >
                                            {d.decision}
                                          </Badge>
                                        </Tooltip>
                                      ) : (
                                        <Text size="xs" c="dimmed">--</Text>
                                      )}
                                    </Table.Td>
                                  )
                                })}
                              </Table.Tr>
                            )
                          })}
                        </Table.Tbody>
                      </Table>
                    </ScrollArea>
                  </Stack>
                )}

                {decisionsQuery.isSuccess && allOffenseIds.length === 0 && (
                  <Text size="sm" c="dimmed">No offense-level decisions found for this search.</Text>
                )}
              </Stack>
            </ScrollArea>
          )}
        </Tabs.Panel>

        {/* Feedback Tab (Expert Mode Only) */}
        {isExpertMode && feedbackRequest && (
          <Tabs.Panel value="feedback" pt="md">
            <ScrollArea h={500}>
              <Stack gap="md">
                {!isCompleted ? (
                  <>
                    <Select
                      label="Your Decision"
                      placeholder="Select decision"
                      required
                      value={decision}
                      onChange={(val) => setDecision(val as 'REPORTABLE' | 'NOT_REPORTABLE' | 'REQUIRES_REVIEW' | 'UNCLEAR')}
                      data={[
                        { value: 'REPORTABLE', label: 'Reportable' },
                        { value: 'NOT_REPORTABLE', label: 'Not Reportable' },
                        { value: 'REQUIRES_REVIEW', label: 'Requires Review' },
                        { value: 'UNCLEAR', label: 'Unclear' },
                      ]}
                    />

                    <Textarea
                      label="Your Feedback"
                      placeholder="Provide detailed feedback about your decision..."
                      required
                      value={feedback}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      minRows={4}
                      maxRows={8}
                    />

                    <Checkbox
                      label="Ground truth is invalid"
                      description="Check this if the existing ground truth label is incorrect"
                      checked={groundTruthInvalid}
                      onChange={(e) => setGroundTruthInvalid(e.currentTarget.checked)}
                    />

                    <Button
                      leftSection={<IconSend size={16} />}
                      onClick={handleSubmitFeedback}
                      loading={submitFeedbackMutation.isPending}
                      disabled={!decision || !feedback.trim()}
                      fullWidth
                    >
                      Submit Feedback
                    </Button>
                  </>
                ) : (
                  <>
                    <Text fw={600} size="lg" c="green">Feedback Submitted</Text>

                    <Stack gap="xs">
                      <Text fw={600} size="sm">Decision:</Text>
                      <Badge size="lg" color="blue">
                        {feedbackRequest.expert_decision}
                      </Badge>
                    </Stack>

                    <Stack gap="xs">
                      <Text fw={600} size="sm">Feedback:</Text>
                      <Text size="sm">{feedbackRequest.expert_feedback}</Text>
                    </Stack>

                    {feedbackRequest.ground_truth_invalid && (
                      <Badge color="red" size="lg">
                        Ground Truth Flagged as Invalid
                      </Badge>
                    )}

                    <Stack gap="xs">
                      <Text fw={600} size="sm">Submitted by:</Text>
                      <Text size="sm">{feedbackRequest.assigned_to_email}</Text>
                    </Stack>

                    <Stack gap="xs">
                      <Text fw={600} size="sm">Completed at:</Text>
                      <Text size="sm">
                        {feedbackRequest.completed_at ? new Date(feedbackRequest.completed_at).toLocaleString() : 'N/A'}
                      </Text>
                    </Stack>
                  </>
                )}
              </Stack>
            </ScrollArea>
          </Tabs.Panel>
        )}
        </Tabs>
      </Stack>
    </Modal>
  )
}
